import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { gallery, galleryCategories, waLink } from "@/content/site";
import { Reveal, SectionHead, WhatsAppIcon, Arrow } from "@/components/apex/primitives";
import MediaViewer from "@/components/apex/MediaViewer";

const Gallery = () => {
  const { t, isRtl } = useLang();
  const [cat, setCat] = useState("all");
  const [viewIndex, setViewIndex] = useState(null);

  const items = useMemo(
    () => (cat === "all" ? gallery : gallery.filter((g) => g.category === cat)),
    [cat],
  );

  const askMsg = (item) =>
    isRtl
      ? `مرحبًا APEX، أعجبني تصميم «${item.title_ar}» وأرغب بمعرفة الخيارات والسعر المناسب لمدخلي.`
      : `Hello APEX, I liked the "${item.title_en}" design and would like to know the options and a suitable price for my entrance.`;

  return (
    <section id="collections" data-testid="gallery-section" className="relative bg-[var(--cream-2)] py-14 sm:py-20">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <SectionHead align="center" label={t.gallery.label} title={t.gallery.title} body={t.gallery.body} />

        <Reveal delay={0.1}>
          <div role="group" aria-label={t.gallery.filterAria} className="mt-10 flex flex-wrap justify-center gap-2.5">
            {galleryCategories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                aria-pressed={cat === c.id}
                data-testid={`gallery-filter-${c.id}`}
                className={`ap-filter ${cat === c.id ? "ap-filter-on" : ""}`}
              >
                {isRtl ? c.label_ar : c.label_en}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {items.map((item, i) => (
              <motion.article
                layout
                key={item.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
                className="ap-card ap-card-hover group flex flex-col p-3"
                data-testid={`gallery-card-${item.id}`}
              >
                <button
                  type="button"
                  onClick={() => setViewIndex(gallery.findIndex((g) => g.id === item.id))}
                  data-testid={`gallery-open-${item.id}`}
                  aria-label={`${t.gallery.view}: ${isRtl ? item.title_ar : item.title_en}`}
                  className="ap-img relative block aspect-[4/5] w-full"
                >
                  <img
                    src={item.src}
                    alt={isRtl ? item.alt_ar : item.alt_en}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                  {item.concept && (
                    <span className="absolute start-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-[var(--text)] shadow">
                      {t.gallery.conceptTag}
                    </span>
                  )}
                  <span className="absolute end-4 top-4 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-[var(--charcoal)] opacity-0 shadow-lg transition-all duration-500 group-hover:opacity-100">
                    <Maximize2 className="h-4 w-4" strokeWidth={2} />
                  </span>
                </button>

                <div className="flex flex-1 flex-col px-3 pb-3 pt-5">
                  <h3 className="ap-display text-[1.3rem] text-[var(--text)]">
                    {isRtl ? item.title_ar : item.title_en}
                  </h3>
                  <p className="mt-2 flex-1 text-[14px] leading-[1.8] text-[var(--muted)]">
                    {isRtl ? item.finish_ar : item.finish_en}
                  </p>
                  <a
                    href={waLink(askMsg(item))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`gallery-ask-${item.id}`}
                    className="ap-link mt-5 text-[14px]"
                  >
                    <WhatsAppIcon className="h-4 w-4 text-[var(--wa)]" />
                    {t.gallery.ask}
                    <Arrow className="!h-4 !w-4" />
                  </a>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>

        <Reveal delay={0.1}>
          <p className="mt-12 flex flex-wrap items-center justify-center gap-3 text-center text-[15px] text-[var(--text)]">
            <span className="rounded-full bg-[var(--yellow)] px-3.5 py-1.5 text-[13px] font-semibold text-[var(--charcoal)]">
              APEX
            </span>
            {t.viz.title}
            <a href="#visualizer" data-testid="gallery-to-visualizer" className="ap-link">
              {t.nav.visualizer}
              <Arrow className="!h-4 !w-4" />
            </a>
          </p>
          <div className="mt-6 text-center">
            <Link to="/designs" data-testid="gallery-all-designs" className="ap-btn-dark">
              {t.gallery.seeAll}
              <Arrow />
            </Link>
          </div>
        </Reveal>
      </div>

      <AnimatePresence>
        {viewIndex !== null && (
          <MediaViewer
            item={gallery[viewIndex]}
            onClose={() => setViewIndex(null)}
            onPrev={() => setViewIndex((i) => (i - 1 + gallery.length) % gallery.length)}
            onNext={() => setViewIndex((i) => (i + 1) % gallery.length)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default Gallery;
